import { Suspense } from "react";
import * as React from "react";
import { Provider } from "react-redux";
import { Switch, Route, Redirect, withRouter, RouteComponentProps } from "react-router-dom";
import { Store } from "redux";
import { History } from "history";
import { polyfill } from "seamless-scroll-polyfill/dist/esm/Element.scrollIntoView";

import { configureStore } from "../store/store";
import GlobalStyles from "../styles/global";
import Apps from "./apps/apps";
import { RootState } from "../store/types";
import * as routes from "../routes";
import { initAppsSaver } from "../lib/apps-saver";
import { loadOrCreateUser } from "../lib/load-user";
import { registerComponentHandlers } from "common/generator/components/register-all";
import { HelpButton } from "./apps/ui/smart/help-button/help-button";
import { getSpotlightUI } from "common/spotlight-ui";
import { setNewComponents } from "../reducers/new-components-state";
import { ConnectStripeReturnScreen } from "./apps/ui/smart/connect-stripe/connect-stripe-return-screen";
import ThemedEnvironment from "./lib/ThemedEnvironment";
import AcceptInviteScreen from "./apps/orgs/accept-invite-screen/accept-invite-screen";
import { getUnaffiliatedDatabase } from "../lib/web-database";
import { registerActionHandlers } from "common/generator/actions/register-all";
import { registerDescriptionHandlers } from "common/generator/components/description-handlers/register-all";
import { setAppFacilities } from "webapp/lib/app-renderer";
import { MaterialAppFacilities } from "webapp/lib/material-app-facilities";
import { setGlobalStoreForLoadedState } from "../store/loaded-redux-state";
import PurchaseTemplate from "./ui/smart/builder-purchases/purchase-template";
import { ConnectStripePreScreen } from "./apps/ui/smart/connect-stripe/connect-stripe-pre-screen";
import { setPageTitleForAppTitle } from "./lib/set-page-title";
import { removeQueryStrings, getQueryStrings } from "lib/browser-hacks";
import { setReferralID } from "lib/firebase-functions";
import AcceptAppScreen from "./apps/ui/smart/accept-app-screen/accept-app-screen";
import { lazilyLoadedComponent } from "lib/react-lazy-loading";
import SupportCodeScreen from "./apps/ui/smart/support-code-screen/support-code-screen";

import { enableMapSet } from "immer";
import { DownloadExportScreen } from "./apps/ui/smart/download-export-screen/download-export-screen";
import { LoadingIndicator } from "./apps/orgs/orgs-styles";
import { styled } from "sharedUI/styles";
import ReactDOM from "react-dom";
import { nonNull } from "common/Support";
import { registerArrayScreenHandlers } from "common/generator/array-screens/register-all";
import { SimpleModalProviderV2 } from "./modals-v2/modal-provider-v2/modal-provider-v2";
import { honeycombSendEvent } from "lib/honeycomb";
import { registerFrontendEventSender } from "common/tracing";
import { BUILDER_BUSY_CONTAINER } from "sharedUI/z-index";
import { ErrorBoundary } from "lib/error-boundary";
import { Toaster } from "react-hot-toast";
enableMapSet();

let store: Store<RootState> | undefined;

interface Props extends RouteComponentProps {
    history: History;
}

const Builder = lazilyLoadedComponent("builder", () => import("./builder"));

let busyCallback: () => void = () => undefined;
let busyCount = 0;
let busyOverride = 0;

export function setPremptBusy() {
    busyOverride++;
    busyCallback();
}

export function stopPremptBusy() {
    if (busyOverride > 0) {
        busyOverride--;
    }
    busyCallback();
}

export function startBusy() {
    busyCount++;
    busyCallback();
}

export function stopBusy() {
    if (busyCount > 0) {
        busyCount--;
    }
    busyCallback();
}

export function useOverridesBusy() {
    React.useEffect(() => {
        setPremptBusy();
        return stopPremptBusy;
    }, []);
}

export async function withBusy<T>(f: (setNotBusy: () => void, setBusy: () => void) => Promise<T>): Promise<T> {
    let isBusy = false;
    function setBusy() {
        if (isBusy) return;
        isBusy = true;
        startBusy();
    }
    function setNotBusy() {
        if (!isBusy) return;
        isBusy = false;
        stopBusy();
    }
    try {
        setBusy();
        return await f(setNotBusy, setBusy);
    } finally {
        setNotBusy();
    }
}

const BusyContainer = styled.div`
    width: 100vw;
    height: 100vh;
    position: absolute;
    top: 0;
    left: 0;
    background-color: ${p => p.theme.bgColorLight}DD;

    z-index: ${BUILDER_BUSY_CONTAINER};
`;

class App extends React.Component<Props> {
    public constructor(props: Props) {
        super(props);
        registerFrontendEventSender(honeycombSendEvent, true);
        registerComponentHandlers();
        registerArrayScreenHandlers();
        registerActionHandlers();
        registerDescriptionHandlers();
        setAppFacilities(new MaterialAppFacilities());
        if (!("scrollBehavior" in document.documentElement.style)) {
            polyfill({});
        }
        const referral = getQueryStrings().get("referral");
        if (referral !== null) {
            setReferralID(referral);
            removeQueryStrings("referral");
        }

        busyCallback = () => this.forceUpdate();
    }

    public render() {
        if (store === undefined) {
            enableMapSet();
            store = configureStore(this.props.history);

            initAppsSaver(store, this.props.history);
            setGlobalStoreForLoadedState(store);
            loadOrCreateUser(store);

            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            getSpotlightUI(getUnaffiliatedDatabase()).then(v => {
                if (v !== undefined && store !== undefined) {
                    store.dispatch(setNewComponents(v.newComponents));
                }
            });
        }
        if (!window.location.pathname.startsWith("/app/")) {
            // FIXME: This is a hack to clear page titles when not editing an app.
            // There's got to be a better way to do this.
            setPageTitleForAppTitle(undefined);
        }

        let busyPortal: React.ReactNode;
        if (busyCount > 0 && busyOverride <= 0) {
            busyPortal = ReactDOM.createPortal(
                <BusyContainer>
                    <LoadingIndicator />
                </BusyContainer>,
                nonNull(document.getElementById("portal"))
            );
        }

        const toast = ReactDOM.createPortal(
            <Toaster position="bottom-center" toastOptions={{ className: "glide-toast" }} />,
            nonNull(document.getElementById("portal"))
        );

        return (
            <Provider store={store}>
                <ThemedEnvironment>
                    <SimpleModalProviderV2>
                        {busyPortal}
                        {toast}
                        <ErrorBoundary>
                            <Suspense fallback={<div />}>
                                <Switch>
                                    <Route
                                        exact={true}
                                        path={routes.purchaseTemplatePattern}
                                        component={PurchaseTemplate}
                                    />
                                    <Route
                                        exact={true}
                                        path={routes.preConnectStripe}
                                        component={ConnectStripePreScreen}
                                    />
                                    <Route
                                        exact={true}
                                        path={routes.connectStripe}
                                        component={ConnectStripeReturnScreen}
                                    />
                                    <Route exact={true} path={routes.orgAcceptPattern} component={AcceptInviteScreen} />
                                    <Route exact={true} path={routes.acceptAppPattern} component={AcceptAppScreen} />
                                    <Route
                                        exact={true}
                                        path={routes.supportCodePattern}
                                        component={SupportCodeScreen}
                                    />
                                    <Route
                                        exact={true}
                                        path={routes.downloadExportPattern}
                                        component={DownloadExportScreen}
                                    />
                                    <Route exact={true} path={routes.orgsPaths} component={Apps} />
                                    <Route exact={false} path={routes.appPattern} component={Builder} />
                                    <Redirect to="/" />
                                </Switch>
                            </Suspense>
                        </ErrorBoundary>
                        <HelpButton />
                        <GlobalStyles />
                    </SimpleModalProviderV2>
                </ThemedEnvironment>
            </Provider>
        );
    }
}

export default withRouter(App);
